import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import type { ConversacionConPreview, Mensaje, MensajeInsert, ChatMiembro } from '../types/chat'

export const CHAT_ADJUNTO_URL_TTL_SECONDS = 600

export const chatService = {
  /**
   * Listar conversaciones del usuario con preview del último mensaje,
   * contador de no leídos y estado muted. Consolidado en 1 RPC
   * (M3 chat-fixes) — antes eran 4 queries separadas.
   */
  async listarConversaciones(
    client: SupabaseClient<Database>,
    organizacionId: string,
    userEmail: string
  ): Promise<ConversacionConPreview[]> {
    const { data, error } = await client.rpc('chat_listar_conversaciones', {
      p_user_email: userEmail,
      p_org_id: organizacionId,
    })

    if (error) throw error
    return (data ?? []) as unknown as ConversacionConPreview[]
  },

  /**
   * Obtener o crear conversación directa 1:1
   */
  async obtenerOCrearDirecta(
    client: SupabaseClient<Database>,
    organizacionId: string,
    userEmail: string,
    otherEmail: string,
    attempts = 0
  ): Promise<string> {
    // H3: límite de recursión para evitar loop infinito en race repetida
    if (attempts > 3) {
      throw new Error('No se pudo crear conversación directa tras 3 intentos (posible race condition)')
    }

    // F-04: Validate both users belong to same org
    // (app-layer multi-tenancy — same pattern as rest of project)
    if (userEmail === otherEmail) {
      throw new Error('No se puede crear conversación consigo mismo')
    }

    // Check if conversation already exists between these two users
    const { data: myConvs } = await client.from('chat_miembros')
      .select('conversacion_id')
      .eq('user_email', userEmail)

    if (myConvs?.length) {
      const myConvIds = myConvs.map((m: { conversacion_id: string }) => m.conversacion_id)

      const { data: otherConvs } = await client.from('chat_miembros')
        .select('conversacion_id')
        .eq('user_email', otherEmail)
        .in('conversacion_id', myConvIds)

      if (otherConvs?.length) {
        const { data: conv } = await client.from('chat_conversaciones')
          .select('id')
          .eq('id', otherConvs[0].conversacion_id)
          .eq('organizacion_id', organizacionId)
          .eq('tipo', 'direct')
          .single()

        if (conv) return conv.id
      }
    }

    // F-05: Use upsert-like pattern to avoid race condition duplicates
    //
    // El id se genera acá y NO se pide RETURNING: con RLS por membresía, el `.select()` de un
    // INSERT evalúa la policy de SELECT cuando la conversación todavía no tiene miembros, y
    // devuelve 42501. `created_by` es lo que habilita al creador a insertar esa primera fila
    // de miembros (policy chat_miembros_insert → chat_es_creador).
    const convId = crypto.randomUUID()

    const { error: convError } = await client.from('chat_conversaciones')
      .insert({ id: convId, organizacion_id: organizacionId, tipo: 'direct', created_by: userEmail })

    if (convError) throw convError

    // Add both members (unique constraint prevents duplicates)
    const { error: membError } = await client.from('chat_miembros')
      .insert([
        { conversacion_id: convId, user_email: userEmail },
        { conversacion_id: convId, user_email: otherEmail },
      ])

    if (membError) {
      // If duplicate constraint, conversation was created by race condition — find it
      if (membError.code === '23505') {
        return this.obtenerOCrearDirecta(client, organizacionId, userEmail, otherEmail, attempts + 1)
      }
      throw membError
    }

    return convId
  },

  /**
   * Obtener mensajes con cursor pagination (más recientes primero)
   */
  async obtenerMensajes(
    client: SupabaseClient<Database>,
    conversacionId: string,
    userEmail: string,
    cursor?: string,
    limit = 30
  ): Promise<Mensaje[]> {
    // F-03: Verify user is member of this conversation before returning messages
    const { data: membership } = await client.from('chat_miembros')
      .select('id')
      .eq('conversacion_id', conversacionId)
      .eq('user_email', userEmail)
      .single()

    if (!membership) {
      throw new Error('No sos miembro de esta conversación')
    }

    let query = client.from('chat_mensajes')
      .select('*')
      .eq('conversacion_id', conversacionId)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (cursor) {
      query = query.lt('created_at', cursor)
    }

    const { data, error } = await query

    if (error) throw error
    return (data ?? []) as Mensaje[]
  },

  /**
   * Trae un mensaje puntual por id.
   *
   * Es la contraparte del broadcast: el canal de Realtime es público —Realtime conecta con la
   * anon key y sin los claims de Auth0, así que un canal privado no admitiría a nadie— y por eso
   * transporta solo `{ id, conversacion_id }`. El contenido se busca acá, donde SÍ hay sesión y
   * la RLS por membresía decide si corresponde entregarlo.
   *
   * Devuelve null si la fila no existe o si RLS la niega. Las dos son el mismo caso desde el
   * lado del cliente y ninguna es un error a mostrar: significa "esto no es para vos".
   */
  async obtenerMensajePorId(
    client: SupabaseClient<Database>,
    mensajeId: string
  ): Promise<Mensaje | null> {
    const { data, error } = await client.from('chat_mensajes')
      .select('*')
      .eq('id', mensajeId)
      .is('deleted_at', null)
      .maybeSingle()

    if (error) throw error
    return (data as Mensaje | null) ?? null
  },

  /**
   * Enviar mensaje
   */
  async enviarMensaje(
    client: SupabaseClient<Database>,
    mensaje: MensajeInsert
  ): Promise<Mensaje> {
    // C3: Verify sender is member before inserting
    const { data: membership } = await client.from('chat_miembros')
      .select('id')
      .eq('conversacion_id', mensaje.conversacion_id)
      .eq('user_email', mensaje.sender_email)
      .single()

    if (!membership) {
      throw new Error('No sos miembro de esta conversación')
    }

    const { data, error } = await client.from('chat_mensajes')
      .insert(mensaje)
      .select()
      .single()

    if (error) throw error

    // Update conversation updated_at
    await client.from('chat_conversaciones')
      .update({ updated_at: new Date().toISOString() })
      .eq('id', mensaje.conversacion_id)

    return data as Mensaje
  },

  /**
   * Marcar conversación como leída
   */
  async marcarLeido(
    client: SupabaseClient<Database>,
    conversacionId: string,
    userEmail: string,
    messageId: string
  ): Promise<void> {
    const { error } = await client.from('chat_miembros')
      .update({ last_read_message_id: messageId })
      .eq('conversacion_id', conversacionId)
      .eq('user_email', userEmail)

    if (error) throw error
  },

  /**
   * Get read receipts for a conversation (who read what)
   */
  async obtenerReadReceipts(
    client: SupabaseClient<Database>,
    conversacionId: string
  ): Promise<{ user_email: string; last_read_message_id: string | null }[]> {
    const { data, error } = await client.from('chat_miembros')
      .select('user_email, last_read_message_id')
      .eq('conversacion_id', conversacionId)

    if (error) throw error
    return data ?? []
  },

  /**
   * Search messages by content (ILIKE).
   * H4 fix: búsqueda global requiere organizacionId para evitar cross-org leak.
   */
  async buscarMensajes(
    client: SupabaseClient<Database>,
    query: string,
    options: {
      conversacionId?: string
      userEmail: string
      organizacionId: string
      limit?: number
    }
  ): Promise<(Mensaje & { conversacion_tipo?: string; conversacion_nombre?: string })[]> {
    const limit = options.limit ?? 20
    const searchTerm = `%${query}%`

    if (options.conversacionId) {
      const { data, error } = await client.from('chat_mensajes')
        .select('*')
        .eq('conversacion_id', options.conversacionId)
        .ilike('content', searchTerm)
        .is('deleted_at', null)
        .order('created_at', { ascending: false })
        .limit(limit)

      if (error) throw error
      return (data ?? []) as Mensaje[]
    }

    // Global: limitar a conversaciones del usuario EN la organización actual.
    // Primero obtenemos IDs de conversaciones de la org actual, luego intersectamos
    // con las membresías del usuario. Dos queries para evitar joins complejos vía cliente.
    const { data: orgConvs } = await client.from('chat_conversaciones')
      .select('id')
      .eq('organizacion_id', options.organizacionId)

    if (!orgConvs?.length) return []
    const orgConvIds = new Set(
      orgConvs.map((c: { id: string }) => c.id)
    )

    const { data: myConvs } = await client.from('chat_miembros')
      .select('conversacion_id')
      .eq('user_email', options.userEmail)

    if (!myConvs?.length) return []

    // Intersección: solo conversaciones donde el user es miembro Y están en la org activa
    const convIds = myConvs
      .map((m: { conversacion_id: string }) => m.conversacion_id)
      .filter((id: string) => orgConvIds.has(id))

    if (convIds.length === 0) return []

    const { data, error } = await client.from('chat_mensajes')
      .select('*')
      .in('conversacion_id', convIds)
      .ilike('content', searchTerm)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
      .limit(limit)

    if (error) throw error
    return (data ?? []) as Mensaje[]
  },

  /**
   * Get Set of conversation IDs where user is a member
   * Used by ChatProvider for client-side notification filtering
   */
  async obtenerMisConversacionIds(
    client: SupabaseClient<Database>,
    userEmail: string
  ): Promise<Set<string>> {
    const { data, error } = await client.from('chat_miembros')
      .select('conversacion_id')
      .eq('user_email', userEmail)

    if (error) throw error
    return new Set((data ?? []).map((m: { conversacion_id: string }) => m.conversacion_id))
  },

  /**
   * Create group conversation with 3+ members
   */
  async crearGrupo(
    client: SupabaseClient<Database>,
    organizacionId: string,
    creatorEmail: string,
    input: { nombre: string; miembros: string[] }
  ): Promise<ConversacionConPreview> {
    if (!input.nombre.trim()) throw new Error('El nombre del grupo es requerido')
    if (input.miembros.length < 1) throw new Error('Seleccioná al menos 1 miembro')

    // Id generado acá: ver el comentario de obtenerOCrearDirecta — con RLS, pedir RETURNING
    // sobre una conversación sin miembros da 42501.
    const convId = crypto.randomUUID()

    const { error: convError } = await client.from('chat_conversaciones')
      .insert({
        id: convId,
        organizacion_id: organizacionId,
        tipo: 'group',
        nombre: input.nombre.trim(),
        created_by: creatorEmail,
      })

    if (convError) throw convError

    const allMembers = [
      { conversacion_id: convId, user_email: creatorEmail, role: 'admin' },
      ...input.miembros
        .filter((email) => email !== creatorEmail)
        .map((email) => ({ conversacion_id: convId, user_email: email, role: 'member' })),
    ]

    const { error: membError } = await client.from('chat_miembros').insert(allMembers)
    if (membError) throw membError

    // ORR-281 — con los miembros ya insertados, el creador tiene membresía y esta consulta
    // no choca con la RLS (a diferencia de un RETURNING sobre el insert de arriba). Devolver
    // la fila completa evita que el caller arme un objeto a mano sin `created_by`/`member_count`,
    // que es lo que hacía aparecer "0 miembros" y ocultaba los controles de admin hasta volver
    // al listado.
    const conversaciones = await this.listarConversaciones(client, organizacionId, creatorEmail)
    const creada = conversaciones.find((c) => c.id === convId)
    if (!creada) throw new Error('El grupo se creó pero no se pudo leer de vuelta')
    return creada
  },

  /**
   * Toggle mute status for a conversation
   */
  async toggleMute(
    client: SupabaseClient<Database>,
    conversacionId: string,
    userEmail: string
  ): Promise<boolean> {
    const { data: member, error: fetchErr } = await client.from('chat_miembros')
      .select('muted')
      .eq('conversacion_id', conversacionId)
      .eq('user_email', userEmail)
      .single()

    if (fetchErr) throw fetchErr

    const newMuted = !member.muted

    const { error } = await client.from('chat_miembros')
      .update({ muted: newMuted })
      .eq('conversacion_id', conversacionId)
      .eq('user_email', userEmail)

    if (error) throw error
    return newMuted
  },

  /**
   * Create org-wide channel (auto-adds all org members)
   */
  async crearChannel(
    client: SupabaseClient<Database>,
    organizacionId: string,
    creatorEmail: string,
    nombre: string,
    orgMemberEmails: string[]
  ): Promise<string> {
    if (!nombre.trim()) throw new Error('El nombre del canal es requerido')

    const convId = crypto.randomUUID()

    const { error: convError } = await client.from('chat_conversaciones')
      .insert({
        id: convId,
        organizacion_id: organizacionId,
        tipo: 'channel',
        nombre: nombre.trim(),
        created_by: creatorEmail,
      })

    if (convError) throw convError

    const members = orgMemberEmails.map((email) => ({
      conversacion_id: convId,
      user_email: email,
      role: email === creatorEmail ? 'admin' : 'member',
    }))

    const { error: membError } = await client.from('chat_miembros').insert(members)
    if (membError) throw membError

    return convId
  },

  // ===========================================
  // Group member management
  // ===========================================

  async listarMiembros(
    client: SupabaseClient<Database>,
    conversacionId: string
  ): Promise<ChatMiembro[]> {
    const { data, error } = await client.from('chat_miembros')
      .select('*')
      .eq('conversacion_id', conversacionId)
      .order('joined_at', { ascending: true })

    if (error) throw error
    // Coerce nullable DB fields to strict app types:
    // - muted: BD permite NULL, app asume false por default
    // - joined_at: BD permite NULL, app fallback al "ahora" (unknown real)
    // - role: BD es text libre, app tipa 'admin' | 'member'. Si aparece un
    //   role desconocido (corrupcion o esquema nuevo), logueamos para que el
    //   default silencioso a 'member' sea visible en monitoreo.
    return (data ?? []).map((row) => {
      if (row.role !== 'admin' && row.role !== 'member') {
        console.warn(
          `[chatService.listarMiembros] role desconocido "${row.role}" en chat_miembros.id=${row.id}, defaulting a 'member'`,
        )
      }
      return {
        id: row.id,
        conversacion_id: row.conversacion_id,
        user_email: row.user_email,
        last_read_message_id: row.last_read_message_id,
        role: (row.role === 'admin' ? 'admin' : 'member') as ChatMiembro['role'],
        muted: row.muted ?? false,
        joined_at: row.joined_at ?? new Date().toISOString(),
      }
    })
  },

  async agregarMiembro(
    client: SupabaseClient<Database>,
    conversacionId: string,
    adminEmail: string,
    newMemberEmail: string
  ): Promise<void> {
    const { data: admin } = await client.from('chat_miembros')
      .select('role')
      .eq('conversacion_id', conversacionId)
      .eq('user_email', adminEmail)
      .single()

    if (!admin || admin.role !== 'admin') {
      throw new Error('Solo administradores pueden agregar miembros')
    }

    const { error } = await client.from('chat_miembros')
      .insert({ conversacion_id: conversacionId, user_email: newMemberEmail, role: 'member' })

    if (error) {
      if (error.code === '23505') throw new Error('El usuario ya es miembro')
      throw error
    }
  },

  async quitarMiembro(
    client: SupabaseClient<Database>,
    conversacionId: string,
    adminEmail: string,
    memberEmail: string
  ): Promise<void> {
    const { data: admin } = await client.from('chat_miembros')
      .select('role')
      .eq('conversacion_id', conversacionId)
      .eq('user_email', adminEmail)
      .single()

    if (!admin || admin.role !== 'admin') {
      throw new Error('Solo administradores pueden quitar miembros')
    }

    if (adminEmail === memberEmail) {
      const { data: admins } = await client.from('chat_miembros')
        .select('id')
        .eq('conversacion_id', conversacionId)
        .eq('role', 'admin')

      if ((admins?.length ?? 0) <= 1) {
        throw new Error('No podés salir siendo el único administrador')
      }
    }

    const { error } = await client.from('chat_miembros')
      .delete()
      .eq('conversacion_id', conversacionId)
      .eq('user_email', memberEmail)

    if (error) throw error
  },

  async cambiarRol(
    client: SupabaseClient<Database>,
    conversacionId: string,
    adminEmail: string,
    memberEmail: string,
    newRole: 'admin' | 'member'
  ): Promise<void> {
    const { data: admin } = await client.from('chat_miembros')
      .select('role')
      .eq('conversacion_id', conversacionId)
      .eq('user_email', adminEmail)
      .single()

    if (!admin || admin.role !== 'admin') {
      throw new Error('Solo administradores pueden cambiar roles')
    }

    if (adminEmail === memberEmail && newRole === 'member') {
      const { data: admins } = await client.from('chat_miembros')
        .select('id')
        .eq('conversacion_id', conversacionId)
        .eq('role', 'admin')

      if ((admins?.length ?? 0) <= 1) {
        throw new Error('Debe haber al menos un administrador')
      }
    }

    const { error } = await client.from('chat_miembros')
      .update({ role: newRole })
      .eq('conversacion_id', conversacionId)
      .eq('user_email', memberEmail)

    if (error) throw error
  },

  // ===========================================
  // File attachments
  // ===========================================

  async subirArchivo(
    client: SupabaseClient<Database>,
    file: File,
    conversacionId: string,
    orgId: string
  ): Promise<{ path: string; type: string; name: string; size: number }> {
    if (file.size > 10 * 1024 * 1024) throw new Error('El archivo excede 10MB')

    const ext = file.name.split('.').pop() ?? 'bin'
    const path = `${orgId}/${conversacionId}/${crypto.randomUUID()}.${ext}`

    const { error } = await client.storage
      .from('chat-attachments')
      .upload(path, file, { contentType: file.type, upsert: false })

    if (error) throw new Error(`Error al subir archivo: ${error.message}`)

    // Retornamos el path (NO un signed URL). El URL firmado se genera
    // on-demand al renderizar via obtenerAttachmentUrl/useAttachmentUrl.
    // Persistir signed URLs causó el bug de attachments rotos a las 1h.
    return {
      path,
      type: file.type,
      name: file.name,
      size: file.size,
    }
  },

  /**
   * Genera un signed URL fresco para descargar un attachment desde el bucket.
   * NO persistir este URL en BD: vence a los CHAT_ADJUNTO_URL_TTL_SECONDS y useAttachmentUrl la renueva.
   */
  async obtenerAttachmentUrl(
    client: SupabaseClient<Database>,
    path: string
  ): Promise<string> {
    const { data, error } = await client.storage
      .from('chat-attachments')
      .createSignedUrl(path, CHAT_ADJUNTO_URL_TTL_SECONDS)

    if (error || !data?.signedUrl) {
      throw new Error(`Error al generar URL del archivo: ${error?.message ?? 'desconocido'}`)
    }
    return data.signedUrl
  },
}
